import React, { useEffect, useState } from "react";
import { Input, Popconfirm, Tooltip, notification } from "antd";
import { CopyOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import LoadingButton from "../../component/LoadingButton";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import {
  fetchOrganizationToken,
  regenerateOrganizationToken,
} from "../../redux/Services/OrganizationServices";

const MASK = "••••••••••••••••••••••••••••••••";

/**
 * API token (the `token` header callers send). Hidden until revealed;
 * Regenerate replaces it and the old one stops working.
 *
 * Default: partner token of an organization (`organizationId`).
 * For another token (e.g. a facility's) pass `tokenKey` (reloads when it
 * changes), `fetchToken` / `regenerateToken`, `title`, `description`.
 */
const ApiTokenSection = ({
  organizationId,
  tokenKey = organizationId,
  fetchToken = () => fetchOrganizationToken(organizationId),
  regenerateToken = () => regenerateOrganizationToken(organizationId),
  title = "API Token",
  entityLabel = "organization",
  description,
}) => {
  const [token, setToken] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setToken(null);
    setLoaded(false);
    setVisible(false);
    if (!tokenKey) return;
    fetchToken().then((value) => {
      setToken(value);
      setLoaded(true);
    });
    // reload only when the token owner changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenKey]);

  const [handleRegenerate, regenerating] = useAsyncAction(async () => {
    const value = await regenerateToken();
    if (value) {
      setToken(value);
      setVisible(true);
    }
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      notification["success"]({ message: "Token copied" });
    } catch (e) {
      notification["error"]({ message: "Could not copy. Select the token and copy it." });
    }
  };

  return (
    <>
      <div className="flex align-items-center">
        <h3 className="flex1">{title}</h3>
        <Popconfirm
          title={
            token ? (
              <>
                Generate a new token?
                <br />
                The current token stops working immediately.
              </>
            ) : (
              `Generate a token for this ${entityLabel}?`
            )
          }
          okText={token ? "Regenerate" : "Generate"}
          okButtonProps={token ? { danger: true } : {}}
          onConfirm={handleRegenerate}
        >
          <span>
            <LoadingButton type="button" className="gradient-button w-auto" loading={regenerating}>
              {token ? "Regenerate" : "Generate"}
            </LoadingButton>
          </span>
        </Popconfirm>
      </div>
      <p className="mt-10" style={{ color: "#666", fontSize: 13 }}>
        {description || (
          <>
            Partners send this in the <code>token</code> header when calling the external APIs
            (e.g. <code>/api/v3/external/encrypt-user-info</code>). It only works for this
            organization's facilities and records.
          </>
        )}
      </p>
      {!loaded ? (
        <p>Loading...</p>
      ) : token ? (
        <Input
          readOnly
          value={visible ? token : MASK}
          style={{ fontFamily: "monospace" }}
          addonAfter={
            <span className="flex align-items-center" style={{ gap: 12 }}>
              <Tooltip title={visible ? "Hide" : "Show"}>
                <span className="cursor-pointer" onClick={() => setVisible(!visible)}>
                  {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              </Tooltip>
              <Tooltip title="Copy">
                <span className="cursor-pointer" onClick={copy}>
                  <CopyOutlined />
                </span>
              </Tooltip>
            </span>
          }
        />
      ) : (
        <p style={{ color: "#888" }}>No token yet. Click Generate to create one.</p>
      )}
    </>
  );
};

export default ApiTokenSection;
